import React, { useEffect, useState } from "react";

import { Box, ButtonBase, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

import { SubtitleStyles } from "../../types";
import CaptionSample from "./CaptionSample";
import { CAPTION_PRESETS, CaptionPreset, applyPreset } from "./presets";

const SAMPLE_WORDS = ["This", "changes", "everything", "you", "know"];

interface CaptionPresetsProps {
  styles: SubtitleStyles;
  onUpdateStyles: (styles: SubtitleStyles) => void;
}

export default function CaptionPresets({
  styles,
  onUpdateStyles,
}: CaptionPresetsProps) {
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const id = window.setInterval(() => setTick((t) => t + 1), 420);
    return () => window.clearInterval(id);
  }, []);

  const handleSelect = (preset: CaptionPreset) =>
    onUpdateStyles(applyPreset(styles, preset));

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        One click applies font, colors and animation. Fine-tune anything in the
        Style tab.
      </Typography>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(132px, 1fr))",
          gap: 1.25,
        }}
      >
        {CAPTION_PRESETS.map((preset, presetIndex) => {
          const isSelected = styles.preset_id === preset.id;
          // Offset each card so the gallery doesn't animate in lockstep.
          const activeIndex = (tick + presetIndex) % SAMPLE_WORDS.length;
          const words =
            (preset.styles.max_words_per_caption ?? 0) > 0 &&
            preset.styles.caption_animation !== "single_word"
              ? SAMPLE_WORDS.slice(
                  0,
                  Math.max(2, preset.styles.max_words_per_caption!),
                )
              : SAMPLE_WORDS;
          return (
            <ButtonBase
              key={preset.id}
              onClick={() => handleSelect(preset)}
              focusRipple
              sx={{
                display: "block",
                textAlign: "left",
                borderRadius: 2,
                overflow: "hidden",
                border: 2,
                borderColor: isSelected ? "primary.main" : "divider",
                transition: "border-color 120ms, transform 120ms",
                "&:hover": {
                  borderColor: isSelected ? "primary.main" : "text.secondary",
                  transform: "translateY(-1px)",
                },
              }}
            >
              <Box
                sx={{
                  position: "relative",
                  height: 96,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  px: 1,
                  background:
                    "radial-gradient(120% 90% at 30% 20%, #475569 0%, #1e293b 45%, #0f172a 100%)",
                  overflow: "hidden",
                }}
              >
                <CaptionSample
                  words={words}
                  activeIndex={activeIndex % words.length}
                  styles={{ ...preset.styles }}
                  fontSize={Math.min(22, (preset.styles.size ?? 28) * 0.5)}
                />
                {isSelected && (
                  <CheckCircleIcon
                    sx={{
                      position: "absolute",
                      top: 6,
                      right: 6,
                      fontSize: 18,
                      color: "primary.main",
                      bgcolor: "common.white",
                      borderRadius: "50%",
                    }}
                  />
                )}
              </Box>
              <Box
                sx={{
                  px: 1.25,
                  py: 0.75,
                  bgcolor: (theme) =>
                    isSelected
                      ? alpha(theme.palette.primary.main, 0.08)
                      : "background.paper",
                }}
              >
                <Typography variant="subtitle2" sx={{ lineHeight: 1.3 }}>
                  {preset.name}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", lineHeight: 1.3 }}
                  noWrap
                >
                  {preset.tagline}
                </Typography>
              </Box>
            </ButtonBase>
          );
        })}
      </Box>
    </Box>
  );
}
